
import { regionalText } from '@/lib/regional-copy'/**
 * WebAuthn / Passkey helpers.
 *
 * Handles base64url encoding/decoding, conversion between server responses
 * and Web Authentication API types, and the full registration/authentication flows.
 */

// ── Base64URL ──────────────────────────────────────────────────────

function base64urlToBuffer(base64url: string): ArrayBuffer {
  // Add padding if needed
  const padding = '='.repeat((4 - (base64url.length % 4)) % 4)
  const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/') + padding
  const binary = atob(base64)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i)
  }
  return bytes.buffer
}

function bufferToBase64url(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer)
  let binary = ''
  for (const byte of bytes) {
    binary += String.fromCharCode(byte)
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

// ── Feature Detection ──────────────────────────────────────────────

export function isWebAuthnSupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.PublicKeyCredential !== 'undefined'
  )
}

// ── Registration ───────────────────────────────────────────────────

interface RegisterBeginResponse {
  publicKey: {
    rp: { id: string; name: string }
    user: { id: string; name: string; displayName: string }
    challenge: string
    pubKeyCredParams: Array<{ type: string; alg: number }>
    timeout: number
    authenticatorSelection: {
      residentKey: string
      userVerification: string
    }
    attestation: string
    excludeCredentials: Array<{ type: string; id: string; transports?: string[] }>
  }
  challenge_token: string
}

export async function registerPasskey(
  apiBase: string,
  friendlyName?: string,
  fetchOptions?: RequestInit,
): Promise<void> {
  // Step 1: Get registration options from server
  const beginRes = await fetch(`${apiBase}/register/begin`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    ...fetchOptions,
  })
  if (!beginRes.ok) {
    const err = await beginRes.json().catch(() => ({ detail: 'Failed to start registration' }))
    throw new Error(err.detail || 'Failed to start registration')
  }
  const beginData: RegisterBeginResponse = await beginRes.json()

  // Step 2: Convert server response to PublicKeyCredentialCreationOptions
  const publicKey = beginData.publicKey
  const createOptions: PublicKeyCredentialCreationOptions = {
    rp: publicKey.rp,
    user: {
      id: base64urlToBuffer(publicKey.user.id),
      name: publicKey.user.name,
      displayName: publicKey.user.displayName,
    },
    challenge: base64urlToBuffer(publicKey.challenge),
    pubKeyCredParams: publicKey.pubKeyCredParams as PublicKeyCredentialParameters[],
    timeout: publicKey.timeout,
    authenticatorSelection: publicKey.authenticatorSelection as AuthenticatorSelectionCriteria,
    attestation: publicKey.attestation as AttestationConveyancePreference,
    excludeCredentials: publicKey.excludeCredentials.map((c) => ({
      type: 'public-key' as const,
      id: base64urlToBuffer(c.id),
      transports: c.transports as AuthenticatorTransport[] | undefined,
    })),
  }

  // Step 3: Create credential via WebAuthn API
  const credential = (await navigator.credentials.create({
    publicKey: createOptions,
  })) as PublicKeyCredential | null

  if (!credential) throw new Error(regionalText('Passkey creation was cancelled'))

  const attestationResponse = credential.response as AuthenticatorAttestationResponse

  // Step 4: Send attestation to server for verification
  const completeRes = await fetch(`${apiBase}/register/complete`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      challenge_token: beginData.challenge_token,
      credential: {
        id: credential.id,
        rawId: bufferToBase64url(credential.rawId),
        type: credential.type,
        response: {
          attestationObject: bufferToBase64url(attestationResponse.attestationObject),
          clientDataJSON: bufferToBase64url(attestationResponse.clientDataJSON),
        },
      },
      friendly_name: friendlyName || 'Passkey',
    }),
    ...fetchOptions,
  })

  if (!completeRes.ok) {
    const err = await completeRes.json().catch(() => ({ detail: 'Registration failed' }))
    throw new Error(err.detail || 'Registration failed')
  }
}

// ── Authentication ─────────────────────────────────────────────────

interface AuthenticateBeginResponse {
  publicKey: {
    rpId: string
    challenge: string
    timeout: number
    userVerification: string
    allowCredentials: Array<{ type: string; id: string; transports?: string[] }>
  }
  challenge_token: string
}

interface TokenResponse {
  access_token: string
  token_type: string
  refresh_token: string
  expires_in: number
}

export async function authenticateWithPasskey(
  apiBase: string,
  email?: string,
): Promise<TokenResponse> {
  // Step 1: Get authentication options from server
  const beginRes = await fetch(`${apiBase}/authenticate/begin`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(email ? { email } : {}),
  })
  if (!beginRes.ok) {
    const err = await beginRes.json().catch(() => ({ detail: 'Failed to start authentication' }))
    throw new Error(err.detail || 'Failed to start authentication')
  }
  const beginData: AuthenticateBeginResponse = await beginRes.json()

  // Step 2: Convert server response to PublicKeyCredentialRequestOptions
  const publicKey = beginData.publicKey
  const getOptions: PublicKeyCredentialRequestOptions = {
    rpId: publicKey.rpId,
    challenge: base64urlToBuffer(publicKey.challenge),
    timeout: publicKey.timeout,
    userVerification: publicKey.userVerification as UserVerificationRequirement,
    allowCredentials: publicKey.allowCredentials.map((c) => ({
      type: 'public-key' as const,
      id: base64urlToBuffer(c.id),
      transports: c.transports as AuthenticatorTransport[] | undefined,
    })),
  }

  // Step 3: Get assertion via WebAuthn API
  const credential = (await navigator.credentials.get({
    publicKey: getOptions,
  })) as PublicKeyCredential | null

  if (!credential) throw new Error(regionalText('Passkey authentication was cancelled'))

  const assertionResponse = credential.response as AuthenticatorAssertionResponse

  // Step 4: Send assertion to server for verification
  const completeRes = await fetch(`${apiBase}/authenticate/complete`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      challenge_token: beginData.challenge_token,
      credential: {
        id: credential.id,
        rawId: bufferToBase64url(credential.rawId),
        type: credential.type,
        response: {
          authenticatorData: bufferToBase64url(assertionResponse.authenticatorData),
          clientDataJSON: bufferToBase64url(assertionResponse.clientDataJSON),
          signature: bufferToBase64url(assertionResponse.signature),
          userHandle: assertionResponse.userHandle
            ? bufferToBase64url(assertionResponse.userHandle)
            : null,
        },
      },
    }),
  })

  if (!completeRes.ok) {
    const err = await completeRes.json().catch(() => ({ detail: 'Authentication failed' }))
    throw new Error(err.detail || 'Authentication failed')
  }

  return completeRes.json()
}

// ── Credential Management ──────────────────────────────────────────

export interface PasskeyCredential {
  id: number
  friendly_name: string
  created_at: string
  last_used_at: string | null
  backed_up: boolean
}

export async function listPasskeys(
  apiBase: string,
  fetchOptions?: RequestInit,
): Promise<PasskeyCredential[]> {
  const res = await fetch(`${apiBase}/credentials`, {
    credentials: 'include',
    ...fetchOptions,
  })
  if (!res.ok) return []
  const data = await res.json()
  return data.credentials || []
}

export async function deletePasskey(
  apiBase: string,
  id: number,
  fetchOptions?: RequestInit,
): Promise<void> {
  const res = await fetch(`${apiBase}/credentials/${id}`, {
    method: 'DELETE',
    credentials: 'include',
    ...fetchOptions,
  })
  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: 'Failed to remove passkey' }))
    throw new Error(err.detail || 'Failed to remove passkey')
  }
}

export async function renamePasskey(
  apiBase: string,
  id: number,
  friendlyName: string,
  fetchOptions?: RequestInit,
): Promise<void> {
  const res = await fetch(`${apiBase}/credentials/${id}`, {
    method: 'PUT',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ friendly_name: friendlyName }),
    ...fetchOptions,
  })
  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: 'Failed to rename passkey' }))
    throw new Error(err.detail || 'Failed to rename passkey')
  }
}
